/**
 * Converts a hexadecimal color value to its RGB components.
 * @param {string} hex - The hexadecimal color string (e.g., "#3498db").
 * @returns {Object} An object with r, g, and b properties representing red, green, and blue values.
 */

export function hexToRgb(hex: string) {
  hex = hex.replace(/^#/, '');

  let bigint = parseInt(hex, 16);
  let r = (bigint >> 16) & 255;
  let g = (bigint >> 8) & 255;
  let b = bigint & 255;

  return {r, g, b};
}

/**
 * Determines if a color is dark or light based on its hexadecimal value.
 * Uses the perceived brightness formula to calculate luminance.
 * @param {string} hex - The hexadecimal color string (e.g., "#3498db").
 * @returns {boolean} True if the color is dark, false if it is light.
 */
export function isColorDark(hex: string) {
  let {r, g, b} = hexToRgb(hex);

  let luminosidad = 0.299 * r + 0.587 * g + 0.114 * b;

  return luminosidad < 128;
}

/**
 * Determines if a color is white.
 * @param {string} color - The color string.
 * @returns {boolean} True if the color is white, false if it is not.
 */
export function isWhite(color: string) {
  color = color.trim().toLowerCase();

  if (color === 'white' || color === '#fff' || color === '#ffffff') return true;

  // RGB or RGBA
  const rgbRegex = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(,\s*[\d.]+)?\)$/;
  const rgbMatch = color.match(rgbRegex);
  if (rgbMatch) {
    const [r, g, b] = rgbMatch.slice(1, 4).map(Number);
    return r === 255 && g === 255 && b === 255;
  }

  // HSL or HSLA
  const hslRegex =
    /^hsla?\(\s*(\d+),\s*([\d.]+)%?,\s*([\d.]+)%?(,\s*[\d.]+)?\)$/;
  const hslMatch = color.match(hslRegex);
  if (hslMatch) {
    const [h, s, l] = hslMatch.slice(1, 4).map(Number);
    return h === 0 && s === 0 && l === 100;
  }

  return false;
}
